import { useEffect, useRef, useState } from 'preact/hooks';
import { ApiError, listAllRecordsByType, type RecordRow } from '../lib/api';
import { ListSearch, matches } from './ListSearch';
import { routePath } from '../routes/route';
import { activeOnly } from '../lib/retired';
import './FindSheet.css';

// S12b PART C — A SEARCH YOU CAN SEE.
//
// S12a gave each list its own box. This is the shell-level door, and the
// reason it exists in this shape is Huan's own correction: "⌘K opens it —
// how about on a phone?" A keyboard shortcut cannot be the only way in.
// Same rule as wf01's A3 ("the Knot must never be the only door"): whatever
// a shortcut reaches, a visible control must reach. So FindEntry is a real
// tappable target in the shell, and ⌘K is its ACCELERATOR — App.tsx used to
// point that shortcut at the capture sheet, which was a promise the Knot's
// classifier could not keep (its intents are CREATE targets; asking it to
// find a client produced a note). The sheet keeps the Knot. ⌘K stops lying.
//
// THE SEARCH ITSELF IS S12a's — ListSearch's own box and its own `matches`
// folding, unchanged. There is exactly one search in this product.
//
// WHAT IT COVERS, honestly: the four registries this build can list over
// HTTP — work orders, clients, sites and technicians. It reads the same
// pages the list screens read (all non-deleted rows of a type) and filters
// them in memory, so it finds what the SERVER already lets this membership
// read and nothing else: a row-scoped tech sees the rows their scope
// returns, which is the correct answer, not a hole. Retired records are
// excluded, matching every other list. It does NOT cover visits, pay,
// asks, notes, or anything held only inside a record's fields-of-fields —
// naming that is cheaper than letting someone conclude the search is
// broken.
//
// Voice-find is NOT here (Huan's ruling: search is its own instrument until
// the Knot can actually find things). Nothing in this file touches the
// classifier or the Knot's targets.

interface Registry {
  typeKey: string;
  /** The word this product uses for one of these, in the results list. */
  label: string;
  route: (id: string) => string;
}

const REGISTRIES: Registry[] = [
  {
    typeKey: 'hvac_work_order',
    label: 'Work order',
    route: (id) => routePath({ screen: 'workOrderDetail', id }),
  },
  { typeKey: 'hvac_client', label: 'Client', route: (id) => routePath({ screen: 'clientDetail', id }) },
  { typeKey: 'hvac_site', label: 'Site', route: (id) => routePath({ screen: 'siteDetail', id }) },
  {
    typeKey: 'hvac_tech_profile',
    label: 'Tech',
    route: (id) => routePath({ screen: 'techDetail', id }),
  },
];

/** Everything about a row a person might have typed: its name and its own
 *  short text values. Bounded on purpose — a json blob is not a haystack. */
function haystackOf(row: RecordRow): string[] {
  const out: string[] = [row.display_name];
  for (const value of Object.values(row.fields)) {
    if (typeof value === 'string' && value.length > 0 && value.length <= 120) out.push(value);
    else if (typeof value === 'number') out.push(String(value));
  }
  return out;
}

interface Hit {
  id: string;
  name: string;
  kind: string;
  path: string;
}

export interface FindSheetProps {
  tenantId: string;
  onNavigate: (path: string) => void;
  onClose: () => void;
}

/** How many hits are worth showing before the answer is "type more". */
const HIT_CAP = 40;

export function FindSheet({ tenantId, onNavigate, onClose }: FindSheetProps) {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<{ registry: Registry; row: RecordRow }[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      REGISTRIES.map(async (registry) => {
        // One unreadable registry must not empty the whole sheet — a tech
        // who cannot list clients should still find their work orders.
        try {
          const loaded = await listAllRecordsByType(tenantId, registry.typeKey);
          return activeOnly(loaded).map((row) => ({ registry, row }));
        } catch {
          return [];
        }
      })
    )
      .then((groups) => {
        if (!cancelled) setRows(groups.flat());
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not reach the server.');
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    const input = boxRef.current?.querySelector('input');
    if (input instanceof HTMLInputElement) input.focus();
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const trimmed = query.trim();
  let hits: Hit[] = [];
  if (trimmed.length > 0 && rows !== null) {
    for (const { registry, row } of rows) {
      if (!matches(trimmed, haystackOf(row))) continue;
      hits.push({
        id: row.id,
        name: row.display_name,
        kind: registry.label,
        path: registry.route(row.id),
      });
      if (hits.length > HIT_CAP) break;
    }
  }
  const capped = hits.length > HIT_CAP;
  if (capped) hits = hits.slice(0, HIT_CAP);

  return (
    <div class="find-sheet" data-testid="find-sheet">
      <button
        type="button"
        class="find-sheet__scrim"
        aria-label="Close search"
        onClick={onClose}
      />
      <div class="find-sheet__box" ref={boxRef} role="dialog" aria-label="Find">
        <ListSearch
          value={query}
          onChange={setQuery}
          label="Find a work order, client, site or technician"
          placeholder="Find a work order, client, site or tech"
        />

        {error !== null ? <div class="find-sheet__say">{error}</div> : null}

        {error === null && trimmed.length > 0 ? (
          rows === null ? (
            <div class="find-sheet__say">One moment…</div>
          ) : hits.length === 0 ? (
            <div class="find-sheet__say" data-testid="find-none">
              Nothing matching “{trimmed}”.
            </div>
          ) : (
            <div class="find-sheet__hits">
              {hits.map((hit) => (
                <button
                  type="button"
                  class="find-sheet__hit"
                  key={hit.id}
                  onClick={() => {
                    onNavigate(hit.path);
                    onClose();
                  }}
                >
                  <span class="find-sheet__hit-name">{hit.name}</span>
                  <span class="find-sheet__hit-kind">{hit.kind}</span>
                </button>
              ))}
              {capped ? <div class="find-sheet__say">More than {HIT_CAP} matches.</div> : null}
            </div>
          )
        ) : null}
      </div>
    </div>
  );
}

export interface FindEntryProps {
  onOpen: () => void;
}

/** The visible half. Pinned in the shell beside the global create, on the
 *  phone and on the rail alike — the whole point of Part C is that it is
 *  reachable with a thumb. */
export function FindEntry({ onOpen }: FindEntryProps) {
  return (
    <button
      type="button"
      class="find-entry"
      onClick={onOpen}
      aria-label="Find"
      data-testid="find-entry"
    >
      <span aria-hidden="true">⌕</span>
    </button>
  );
}
